// `customFields`: Swift `CustomField` (GroundTeam/Models/Pipeline.swift): the company's extra record fields. A record's
// values for them live in `Record.customValues`, keyed by this id (fieldValue.ts). Decoding template
// `CustomField(name: "")` (GroundTeam/Sync/ResilientDecoding.swift:120).

import { CUSTOM_FIELD_TYPES, RECORD_TYPES, type CustomFieldType, type RecordType } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  INT,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface CustomFieldPayload extends Passthrough {
  id: string;
  name: string;
  type: CustomFieldType;
  /** Choices for dropdown / multiSelect. */
  options: string[];
  appliesTo: RecordType[];
  isRequired: boolean;
  showOnCard: boolean;
  /** Int. */
  order: number;
}

export const CUSTOM_FIELD_KEYS = [
  'id',
  'name',
  'type',
  'options',
  'appliesTo',
  'isRequired',
  'showOnCard',
  'order',
] as const satisfies readonly (keyof Known<CustomFieldPayload>)[];

/** `CustomField.appliesTo` default: every record type. */
const ALL_RECORD_TYPES = (): RecordType[] => [...RECORD_TYPES];

export const CUSTOM_FIELD_FIELDS: FieldMap<CustomFieldPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  type: field.req(enumOf('CustomFieldType', CUSTOM_FIELD_TYPES), () => 'text'),
  options: field.req(arrayOf(STRING), () => []),
  appliesTo: field.req(arrayOf(enumOf('RecordType', RECORD_TYPES)), ALL_RECORD_TYPES),
  isRequired: field.req(BOOL, () => false),
  showOnCard: field.req(BOOL, () => false),
  order: field.req(INT, () => 0),
};

export function decodeCustomField(raw: JsonObject, envelopeId?: string): Decoded<CustomFieldPayload> {
  return decodeModel(CUSTOM_FIELD_FIELDS, raw, {}, envelopeId);
}

export type CustomFieldInit = Partial<Known<CustomFieldPayload>> & { name: string };

export function newCustomField(init: CustomFieldInit): CustomFieldPayload {
  const defaults = {
    id: newId(),
    type: 'text',
    options: [],
    appliesTo: ALL_RECORD_TYPES(),
    isRequired: false,
    showOnCard: false,
    order: 0,
  };
  return createModel(CUSTOM_FIELD_FIELDS, defaults, init, 'customFields');
}
